import React from "react";
import { Box, Typography, Paper } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { Incident } from "./data/monitoring-service";

interface StatusBannerProps {
  unresolvedIncidents: Incident[];
}

const StatusBanner: React.FC<StatusBannerProps> = ({ unresolvedIncidents }) => {
  const getStatusConfig = () => {
    const unresolvedCount = unresolvedIncidents.length;
    const hasCritical = unresolvedIncidents.some(
      (incident) => incident.incidentType === "critical",
    );

    if (unresolvedCount === 0) {
      return {
        label: "OPERATIONAL",
        message: "All systems are operating normally",
        icon: <CheckCircleIcon sx={{ fontSize: 48 }} />,
        color: "#2e7d32",
        bgColor: "#e8f5e9",
      };
    }
    if (hasCritical) {
      const criticalCount = unresolvedIncidents.filter(
        (incident) => incident.incidentType === "critical",
      ).length;
      return {
        label: "MAJOR OUTAGE",
        message: `${criticalCount} critical incident${criticalCount > 1 ? "s" : ""} require immediate attention`,
        icon: <ErrorIcon sx={{ fontSize: 48 }} />,
        color: "#d32f2f",
        bgColor: "#fdecea",
      };
    }
    return {
      label: "DEGRADED",
      message: `${unresolvedCount} warning${unresolvedCount > 1 ? "s" : ""} require${unresolvedCount === 1 ? "s" : ""} attention`,
      icon: <WarningAmberIcon sx={{ fontSize: 48 }} />,
      color: "#ed6c02",
      bgColor: "#fff4e5",
    };
  };

  const status = getStatusConfig();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 3,
        border: "1px solid",
        borderColor: "grey.200",
        borderRadius: 2,
        background: status.bgColor,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            backgroundColor: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: status.color,
            flexShrink: 0,
          }}
        >
          {status.icon}
        </Box>
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: status.color,
              mb: 0.5,
              fontSize: "1.5rem",
            }}
          >
            {status.label}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              fontSize: "0.9rem",
            }}
          >
            {status.message}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default StatusBanner;
